import React, { useCallback, useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, Legend } from 'recharts';
import { useLang } from '../LangContext';
import { statusText } from '../i18n';
import { canSeeMoney, isOrderInScope } from '../adminAccess';
import DatabaseError from '../components/DatabaseError';

const COLORS = ['#351044','#789B55','#D9B447','#6A3977','#A8B98D','#8E6F13'];

const STATUS_COLORS = {
  pending: '#F59E0B', confirmed: '#3B82F6', assigned: '#8B5CF6',
  accepted: '#6366F1', worker_on_route: '#0EA5E9', in_progress: '#F97316',
  before_photos_uploaded: '#EC4899', after_photos_uploaded: '#14B8A6',
  completed: '#10B981', cancelled: '#EF4444',
};

export default function DashboardPage({ user }) {
  const { tr, lang } = useLang();
  const [stats, setStats] = useState({ orders: 0, workers: 0, customers: 0, revenue: 0, pending: 0, completed: 0, inProgress: 0, cancelled: 0 });
  const [recentOrders, setRecentOrders] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [workerData, setWorkerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dbError, setDbError] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    setDbError('');
    try {
      const [workersSnap, customersSnap, ordersSnap] = await Promise.all([
        getDocs(query(collection(db, 'users'), where('role', '==', 'worker'))),
        getDocs(query(collection(db, 'users'), where('role', '==', 'customer'))),
        getDocs(query(collection(db, 'orders'), orderBy('createdAt', 'desc'), limit(10))),
      ]);
      setStats(prev => ({ ...prev, workers: workersSnap.size, customers: customersSnap.size }));
      setRecentOrders(ordersSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(o => isOrderInScope(user, o)));

      // Worker activity
      const workerList = workersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const allOrders = await getDocs(collection(db, 'orders'));
      const orderData = allOrders.docs.map(d => ({ id: d.id, ...d.data() })).filter(o => isOrderInScope(user, o));
      const wData = workerList.map(w => ({
        name: (w.name || 'Worker').split(' ')[0],
        completed: orderData.filter(o => o.workerId === (w.uid || w.id) && o.status === 'completed').length,
        active: orderData.filter(o => o.workerId === (w.uid || w.id) && o.status !== 'completed' && o.status !== 'cancelled' && o.workerId).length,
      })).filter(w => w.completed + w.active > 0);
      setWorkerData(wData);
    } catch (e) {
      console.error(e);
      setDbError(e?.code === 'permission-denied'
        ? 'Permission denied. Please log out and log in again as the super admin.'
        : (e?.message || 'Unable to load dashboard data.'));
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    loadAll();
    // Live orders listener
    const unsub = onSnapshot(collection(db, 'orders'), snap => {
      setDbError('');
      const orders = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(o => isOrderInScope(user, o));
      const completed = orders.filter(o => o.status === 'completed');
      const pending = orders.filter(o => o.status === 'pending');
      const inProg = orders.filter(o => ['assigned','accepted','worker_on_route','in_progress','before_photos_uploaded','after_photos_uploaded'].includes(o.status));
      const cancelled = orders.filter(o => o.status === 'cancelled');
      const revenue = completed.reduce((s, o) => s + (o.totalAmount || 0), 0);
      setStats(prev => ({ ...prev, orders: orders.length, revenue, pending: pending.length, completed: completed.length, inProgress: inProg.length, cancelled: cancelled.length }));

      // Status breakdown
      const statusCount = {};
      orders.forEach(o => { statusCount[o.status] = (statusCount[o.status] || 0) + 1; });
      setStatusData(Object.entries(statusCount).map(([name, value]) => ({ name, value })));

      // Monthly
      const counts = {};
      orders.forEach(o => {
        if (o.createdAt?.toDate) {
          const d = o.createdAt.toDate();
          const key = d.toLocaleDateString('en', { month: 'short', year: '2-digit' });
          counts[key] = (counts[key] || 0) + 1;
        }
      });
      setMonthlyData(Object.entries(counts).slice(-8).map(([name, count]) => ({ name, count })));
    }, err => {
      console.error(err);
      setDbError(err?.code === 'permission-denied'
        ? 'Permission denied. Please log out and log in again as the super admin.'
        : (err?.message || 'Unable to read live order data.'));
      setLoading(false);
    });
    return () => unsub();
  }, [loadAll, user]);

  const showMoney = canSeeMoney(user);
  const STAT_CARDS = [
    { labelKey: 'totalOrders', value: stats.orders, icon: '📋', color: 'text-blue-600', bg: 'bg-blue-50' },
    { labelKey: 'completed', value: stats.completed, icon: '✅', color: 'text-green-600', bg: 'bg-green-50' },
    { labelKey: 'pending', value: stats.pending, icon: '⏳', color: 'text-amber-600', bg: 'bg-amber-50' },
    { labelKey: 'inProgress', value: stats.inProgress, icon: '⚙️', color: 'text-blue-500', bg: 'bg-blue-50' },
    ...(showMoney ? [{ labelKey: 'revenue', value: `${stats.revenue.toFixed(0)} ₼`, icon: '💰', color: 'text-emerald-600', bg: 'bg-emerald-50' }] : []),
    { labelKey: 'workersCount', value: stats.workers, icon: '👷', color: 'text-purple-600', bg: 'bg-purple-50' },
    { labelKey: 'customers', value: stats.customers, icon: '👥', color: 'text-indigo-600', bg: 'bg-indigo-50' },
    { labelKey: 'cancelled', value: stats.cancelled, icon: '❌', color: 'text-red-500', bg: 'bg-red-50' },
  ];

  return (
    <div className="page-shell">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h1 className="page-title">{tr('dashboard')}</h1>
        <button onClick={loadAll} className="btn-primary text-sm">{tr('refresh')}</button>
      </div>

      <DatabaseError message={dbError} onRetry={loadAll} />

      {/* Stat grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {STAT_CARDS.map(s => (
          <div key={s.labelKey} className="stat-card">
            <div className={`w-10 h-10 ${s.bg} rounded-xl flex items-center justify-center text-xl mb-3`}>{s.icon}</div>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{tr(s.labelKey)}</p>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Monthly bar chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="font-semibold text-gray-900 mb-4">{tr('monthlyOrders')}</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" name={tr('orders')} fill="#2D6A4F" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Status pie */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="font-semibold text-gray-900 mb-4">{tr('statusBreakdown')}</h2>
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}>
                  {statusData.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS[entry.name] || COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v, n) => [v, n]} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-gray-400 text-sm text-center py-10">{tr('noData')}</p>}
        </div>
      </div>

      {/* Worker activity */}
      {workerData.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
          <h2 className="font-semibold text-gray-900 mb-4">{tr('workerActivity')}</h2>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={workerData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="completed" name={tr('completed')} fill="#10B981" radius={[4,4,0,0]} />
              <Bar dataKey="active" name={tr('inProgress')} fill="#F97316" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Recent orders */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h2 className="font-semibold text-gray-900 mb-4">{tr('recentOrders')}</h2>
        {loading ? <p className="text-gray-400 text-sm">{tr('loading')}</p> : (
          <div className="overflow-x-auto -mx-1 px-1"><table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-gray-100">
                {[tr('orderNo'), tr('deceased'), tr('cemetery'), tr('amount'), tr('status'), tr('date')].map(h => (
                  <th key={h} className="text-left py-2 pr-4 font-semibold text-gray-500 text-xs uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentOrders.map(o => (
                <tr key={o.id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-2.5 pr-4 font-mono text-xs text-gray-500">{o.orderNumber}</td>
                  <td className="py-2.5 pr-4 font-medium">{o.deceased?.name || '—'}</td>
                  <td className="py-2.5 pr-4 text-gray-600">{o.cemeteryName || '—'}</td>
                  <td className="py-2.5 pr-4 font-semibold text-primary">{showMoney ? `${o.totalAmount?.toFixed(2)} ₼` : 'Hidden'}</td>
                  <td className="py-2.5 pr-4"><StatusChip status={o.status} lang={lang} /></td>
                  <td className="py-2.5 text-gray-400 text-xs">
                    {o.createdAt?.toDate ? o.createdAt.toDate().toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

function StatusChip({ status, lang }) {
  const color = STATUS_COLORS[status] || '#6B7280';
  return (
    <span className="badge text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: color + '20', color }}>
      {statusText(lang, status)}
    </span>
  );
}
